/**
 * Calendar Events API Service
 * CRUD over the org-scoped `/api/calendarevents` endpoints. Multi-tenancy is
 * enforced server-side via the Firebase token + X-Organization-Id header that
 * the shared fetch client injects.
 */

import { get, post, put, del } from "./api";
import type {
  CalendarEventResponse,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  OrganizationMember,
  RecurrenceEditScope,
  RecurrenceScopeCountResponse,
} from "../types/api";

/** Event scope for the calendar: all org events, or only the current user's. */
export type CalendarScope = "all" | "mine";

/**
 * Fetch every event overlapping the [fromIso, toIso) window. Both bounds are
 * ISO-8601 UTC instants. The server returns multi-day events that start before
 * the window but overlap it, so the caller should request a range that covers
 * the full visible calendar grid (leading/trailing days of adjacent months).
 *
 * Pass scope="mine" to limit to events the current user is assigned to.
 */
export const getEvents = async (
  fromIso: string,
  toIso: string,
  scope: CalendarScope = "all",
): Promise<CalendarEventResponse[]> => {
  const params = new URLSearchParams({ from: fromIso, to: toIso });
  if (scope === "mine") {
    params.set("scope", "mine");
  }
  return get<CalendarEventResponse[]>(`/api/calendarevents?${params.toString()}`);
};

/** Org members available to assign to events (any member may call this). */
export const getOrganizationMembers = async (): Promise<OrganizationMember[]> => {
  return get<OrganizationMember[]>("/api/users/organization-members");
};

/** An itinerary attachment with a short-lived signed download URL. */
export type CalendarEventAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  downloadUrl: string;
  createdAt: string;
};

type AttachmentUploadUrl = {
  attachmentId: string;
  uploadUrl: string;
  gcsObjectName: string;
  gcsBucket: string;
};

/** List an event's itinerary attachments. */
export const listAttachments = async (eventId: string): Promise<CalendarEventAttachment[]> => {
  return get<CalendarEventAttachment[]>(`/api/calendarevents/${eventId}/attachments`);
};

/**
 * Upload a PDF itinerary: request a signed URL, PUT the file straight to GCS (no auth header on
 * the signed URL), then register the attachment so a row only exists for a file that landed.
 */
export const uploadAttachment = async (
  eventId: string,
  file: File,
): Promise<CalendarEventAttachment> => {
  const signed = await post<AttachmentUploadUrl>(`/api/calendarevents/${eventId}/attachments/upload-url`, {
    fileName: file.name,
    contentType: "application/pdf",
    fileSizeBytes: file.size,
  });

  const put = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: file,
  });
  if (!put.ok) {
    throw new Error("Upload to storage failed");
  }

  return post<CalendarEventAttachment>(`/api/calendarevents/${eventId}/attachments`, {
    attachmentId: signed.attachmentId,
    gcsObjectName: signed.gcsObjectName,
    fileName: file.name,
    contentType: "application/pdf",
    fileSizeBytes: file.size,
  });
};

/** Soft-delete an attachment. */
export const deleteAttachment = async (eventId: string, attachmentId: string): Promise<void> => {
  return del<void>(`/api/calendarevents/${eventId}/attachments/${attachmentId}`);
};

/** Per-org outcome of an admin distribution. */
export type DistributionOrgResult = {
  organizationId: string;
  organizationName: string | null;
  /**
   * One of `completed`, `skipped_deleted`, `held_mass_delete`, `failed`. **`completed` is the only
   * one that means a copy was written** — the server buckets everything that is neither `completed`
   * nor `failed` into its `skipped` count, so "no failures" is not the same as "it arrived".
   */
  status: string;
  error: string | null;
  /**
   * The itinerary request this share closed for that org, or null if it closed none. Null does not
   * imply a problem: most shares answer no specific request, and a re-push onto an existing copy
   * never attempts fulfilment.
   */
  fulfilledItineraryRequestId: string | null;
};

/** The one per-org status that means a copy actually landed on that organization's calendar. */
export const DISTRIBUTION_COMPLETED = "completed";
/** Summary of an admin distribution run. */
export type DistributeResult = {
  distributionBatchId: string;
  distributed: number;
  skipped: number;
  failed: number;
  results: DistributionOrgResult[];
};

/** Admin-only: the org ids an event is currently distributed to (for pre-selecting the picker). */
export const getDistributionRecipients = async (eventId: string): Promise<string[]> => {
  const res = await get<{ organizationIds: string[] }>(
    `/api/admin/calendar-events/${eventId}/recipients`,
  );
  return res.organizationIds ?? [];
};

/** The sports an organization can be, and the only values `distributeEvent` accepts for `sport`. */
export type OrganizationSport = "baseball" | "softball";

/**
 * The three ways an admin can target a distribution. Exactly one applies: the server's precedence
 * is all > sport > explicit ids, and this union keeps the client from ever sending two at once.
 *
 * Sport targeting resolves to orgs whose recorded sport MATCHES, which deliberately excludes orgs
 * with no sport recorded — letting a null fall into either group is precisely the bug that
 * "no softball program receives the baseball share" exists to prevent.
 */
export type DistributeTarget =
  | { allOrganizations: true }
  | { allOrganizations: false; sport: OrganizationSport }
  | { allOrganizations: false; organizationIds: string[] };

/**
 * Admin-only: distribute an event to all orgs, one sport's orgs, or a subset (system_admin).
 *
 * `itineraryRequestId` closes a SPECIFIC queue row rather than letting the server re-derive it from
 * the tournament's title. The server's fallback matcher is exact case-insensitive title equality,
 * and the build-from-request flow invites a rename — so without it, "build their itinerary and
 * close their request" only closed the row when nobody had touched the name. Omitted for every
 * other kind of share, which still matches by name.
 */
export const distributeEvent = async (
  eventId: string,
  body: DistributeTarget,
  itineraryRequestId?: string,
): Promise<DistributeResult> => {
  // JSON.stringify drops undefined keys, so an absent id simply is not sent.
  return post<DistributeResult>(`/api/admin/calendar-events/${eventId}/distribute`, {
    ...body,
    itineraryRequestId,
  });
};

/** Whether a tournament is published to the shared itinerary catalog. */
export type CatalogPublicationState = { isShareable: boolean };

/**
 * Admin-only: publish a tournament to the shared itinerary catalog, or withdraw it (system_admin).
 *
 * This is the gate the catalog's cross-organization read rests on — nothing else in the API sets
 * `isShareable` — which is why it is a separate admin route rather than a field on the ordinary
 * org-scoped event update. Withdrawing does not touch copies programs already added; those are
 * their own events by then.
 */
export const setCatalogPublication = async (
  eventId: string,
  isShareable: boolean,
): Promise<CatalogPublicationState> => {
  const action = isShareable ? "publish" : "unpublish";
  return post<CatalogPublicationState>(`/api/admin/itinerary-catalog/${eventId}/${action}`, {});
};

/** A location autocomplete suggestion (Google Places, via the backend proxy). */
export type LocationSuggestion = { placeId: string; description: string };
/** Resolved place details, including a derived IANA timezone. */
export type LocationDetails = {
  placeId: string;
  formattedAddress: string;
  lat: number | null;
  lng: number | null;
  timeZone: string | null;
};

/** Autocomplete a location search. Returns [] for short queries or when search is unavailable. */
export const searchLocations = async (query: string): Promise<LocationSuggestion[]> => {
  const params = new URLSearchParams({ q: query });
  return get<LocationSuggestion[]>(`/api/locations/autocomplete?${params.toString()}`);
};

/** Resolve a selected place to address, coordinates, and timezone. */
export const getLocationDetails = async (placeId: string): Promise<LocationDetails> => {
  const params = new URLSearchParams({ placeId });
  return get<LocationDetails>(`/api/locations/details?${params.toString()}`);
};

/** Build a Google Maps directions link from a resolved place (place id preferred, else coords). */
export const directionsUrl = (event: {
  locationPlaceId: string | null;
  locationLat: number | null;
  locationLng: number | null;
  locationAddress: string | null;
  locationName: string | null;
}): string | null => {
  if (event.locationPlaceId) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      event.locationAddress || event.locationName || "",
    )}&destination_place_id=${encodeURIComponent(event.locationPlaceId)}`;
  }
  if (event.locationLat != null && event.locationLng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${event.locationLat},${event.locationLng}`;
  }
  if (event.locationAddress || event.locationName) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      event.locationAddress || event.locationName || "",
    )}`;
  }
  return null;
};

/** Fetch a single event by id (404 if it belongs to another org). */
export const getEvent = async (id: string): Promise<CalendarEventResponse> => {
  return get<CalendarEventResponse>(`/api/calendarevents/${id}`);
};

/** Create a new event; returns the persisted event. */
export const createEvent = async (
  data: CreateCalendarEventRequest,
): Promise<CalendarEventResponse> => {
  return post<CalendarEventResponse>("/api/calendarevents", data);
};

/** Update an existing event; returns the persisted event. */
export const updateEvent = async (
  id: string,
  data: UpdateCalendarEventRequest,
): Promise<CalendarEventResponse> => {
  return put<CalendarEventResponse>(`/api/calendarevents/${id}`, data);
};

/**
 * Soft-delete an event (204 No Content).
 *
 * `scope` only means anything for one occurrence of a repeating series. Omitting it keeps the
 * pre-existing single-row path byte-for-byte, so a non-recurring delete must not pass one.
 */
export const deleteEvent = async (
  id: string,
  scope?: RecurrenceEditScope,
): Promise<void> => {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return del<void>(`/api/calendarevents/${id}${query}`);
};

/**
 * How many live events `deleteEvent(id, scope)` would remove, for the "Deletes N events" line in the
 * scope prompt. Resolves null when the answer is not a usable number (for example an older API that
 * has no such endpoint returns something else), so the caller shows no count rather than a wrong one.
 */
export const getDeleteScopeCount = async (
  id: string,
  scope: RecurrenceEditScope,
): Promise<number | null> => {
  const response = await get<RecurrenceScopeCountResponse>(
    `/api/calendarevents/${id}/scope-count?scope=${encodeURIComponent(scope)}`,
  );
  const count = response?.count;
  return typeof count === "number" && Number.isFinite(count) ? count : null;
};

/**
 * "Assign all to me": add the current coach as an event-level assignee (idempotent, additive). One
 * event-level assignment covers every game via the union model. Returns the updated event.
 */
export const setEventAssignees = async (
  id: string,
  userIds: string[],
): Promise<CalendarEventResponse> => {
  return put<CalendarEventResponse>(`/api/calendarevents/${id}/assignees`, { userIds });
};

export const assignMe = async (id: string): Promise<CalendarEventResponse> => {
  return post<CalendarEventResponse>(`/api/calendarevents/${id}/assignees/me`, {});
};

/**
 * "Remove me": take the current coach off this event AND every one of its games, in one call.
 * The API answers 204 with no body, so refetch the event afterwards if you need its new state.
 */
export const removeMeFromEvent = async (id: string): Promise<void> => {
  return del<void>(`/api/calendarevents/${id}/assignees/me`);
};
